import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  message,
  Modal,
  Tooltip,
  Row,
  Col,
  Descriptions,
  Divider
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  MailOutlined,
  FilePdfOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { billingAdminService, Invoice } from '../../services/billingAdminService';
import { downloadBlob, readBlobError } from '../../utils/fileDownload';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const InvoiceList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: '',
    search: searchParams.get('search') || '',
    startDate: '',
    endDate: ''
  });
  const [detailModal, setDetailModal] = useState<{ visible: boolean; invoice: Invoice | null }>({
    visible: false,
    invoice: null
  });
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.type === 'ADMIN') {
      loadInvoices();
    }
  }, [user, filters, pagination.currentPage]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const result = await billingAdminService.getInvoices({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      });
      setInvoices(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      message.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleVoidInvoice = async (invoice: Invoice) => {
    Modal.confirm({
      title: 'Void Invoice',
      content: `Are you sure you want to void invoice ${invoice.InvoiceNumber}? This action cannot be undone.`,
      okText: 'Void Invoice',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await billingAdminService.voidInvoice(invoice.InvoiceID);
          message.success('Invoice voided successfully');
          loadInvoices();
        } catch (error) {
          message.error('Failed to void invoice');
        }
      }
    });
  };

  const handleSendReminder = async (invoice: Invoice) => {
    try {
      await billingAdminService.sendInvoiceReminder(invoice.InvoiceID);
      message.success('Reminder sent successfully');
    } catch (error) {
      message.error('Failed to send reminder');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await billingAdminService.exportInvoicesCSV(filters);
      downloadBlob(blob, `invoices-${dayjs().format('YYYY-MM-DD')}.csv`);
      message.success('Export completed');
    } catch (error) {
      console.error('Failed to export invoices:', error);
      message.error(await readBlobError(error, 'Export failed'));
    }
  };

  const handleDownloadPDF = async (invoice: Invoice) => {
    try {
      message.loading({ content: 'Generating PDF...', key: 'pdfDownload' });
      await billingAdminService.downloadInvoicePDF(invoice.InvoiceID, invoice.InvoiceNumber);
      message.success({ content: 'PDF downloaded successfully', key: 'pdfDownload' });
    } catch (error) {
      message.error({ content: 'Failed to download PDF', key: 'pdfDownload' });
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'paid': 'green',
      'open': 'blue',
      'draft': 'default',
      'void': 'red',
      'uncollectible': 'orange'
    };
    return statusColors[status] || 'default';
  };

  const columns = [
    {
      title: 'Invoice Number',
      dataIndex: 'InvoiceNumber',
      key: 'InvoiceNumber',
      width: 150,
      render: (text: string, record: Invoice) => (
        <Button type="link" size="small" onClick={() => setDetailModal({ visible: true, invoice: record })}>
          {text}
        </Button>
      )
    },
    {
      title: 'Customer',
      dataIndex: 'CustomerName',
      key: 'CustomerName',
      width: 200,
      render: (name: string, record: Invoice) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.CustomerEmail}</Text>
        </Space>
      )
    },
    {
      title: 'Amount',
      key: 'Amount',
      width: 120,
      render: (_: any, record: Invoice) => (
        <Space direction="vertical" size={0}>
          <Text strong>${record.Total.toFixed(2)}</Text>
          {record.AmountDue > 0 && record.Status !== 'void' && (
            <Text type="danger" style={{ fontSize: '12px' }}>Due: ${record.AmountDue.toFixed(2)}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      )
    },
    {
      title: 'Due Date',
      dataIndex: 'DueDate',
      key: 'DueDate',
      width: 120,
      responsive: ['md'] as const,
      render: (date: string, record: Invoice) => {
        if (!date) return <Text type="secondary">-</Text>;
        const isOverdue = dayjs(date).isBefore(dayjs()) && record.Status === 'open';
        return (
          <Text type={isOverdue ? 'danger' : undefined}>
            {dayjs(date).format('MMM D, YYYY')}
            {isOverdue && ' (Overdue)'}
          </Text>
        );
      }
    },
    {
      title: 'Order',
      dataIndex: 'QuotationRefNo',
      key: 'QuotationRefNo',
      width: 130,
      responsive: ['lg'] as const,
      render: (refNo: string) => refNo ? (
        <Button type="link" size="small" onClick={() => navigate(`/admin/quotations?search=${refNo}`)}>
          {refNo}
        </Button>
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Created',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 140,
      responsive: ['lg'] as const,
      render: (date: string) => dayjs(date).format('MMM D, YYYY')
    },
    {
      title: 'Paid',
      dataIndex: 'PaidAt',
      key: 'PaidAt',
      width: 140,
      responsive: ['lg'] as const,
      render: (date: string) => date ? dayjs(date).format('MMM D, YYYY') : <Text type="secondary">-</Text>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Invoice) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => setDetailModal({ visible: true, invoice: record })}
            />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadPDF(record)}
            />
          </Tooltip>
          {record.Status === 'open' && (
            <>
              <Tooltip title="Send Reminder">
                <Button
                  type="text"
                  size="small"
                  icon={<MailOutlined />}
                  onClick={() => handleSendReminder(record)}
                />
              </Tooltip>
              <Tooltip title="Void Invoice">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleVoidInvoice(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  // Note: Admin access is enforced by AuthGuard at the route level

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Invoices</Title>
          <Text type="secondary">View and manage all invoices</Text>
        </div>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadInvoices}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search by invoice number, customer..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status || undefined}
              onChange={(value) => setFilters({ ...filters, status: value || '' })}
              allowClear
            >
              <Option value="draft">Draft</Option>
              <Option value="open">Open</Option>
              <Option value="paid">Paid</Option>
              <Option value="void">Void</Option>
              <Option value="uncollectible">Uncollectible</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                if (dates) {
                  setFilters({
                    ...filters,
                    startDate: dates[0]?.format('YYYY-MM-DD') || '',
                    endDate: dates[1]?.format('YYYY-MM-DD') || ''
                  });
                } else {
                  setFilters({ ...filters, startDate: '', endDate: '' });
                }
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setFilters({ status: '', search: '', startDate: '', endDate: '' });
                setPagination({ ...pagination, currentPage: 1 });
              }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Invoices Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="InvoiceID"
          loading={loading}
          pagination={{
            current: pagination.currentPage,
            total: pagination.totalItems,
            pageSize: pagination.itemsPerPage,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setPagination({
                ...pagination,
                currentPage: page,
                itemsPerPage: pageSize || 20
              });
            }
          }}
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* Invoice Detail Modal */}
      <Modal
        title={`Invoice: ${detailModal.invoice?.InvoiceNumber}`}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, invoice: null })}
        footer={[
          <Button key="close" onClick={() => setDetailModal({ visible: false, invoice: null })}>
            Close
          </Button>,
          detailModal.invoice?.Status === 'open' && (
            <Button key="remind" icon={<MailOutlined />} onClick={() => detailModal.invoice && handleSendReminder(detailModal.invoice)}>
              Send Reminder
            </Button>
          ),
          <Button key="pdf" icon={<FilePdfOutlined />} type="primary" onClick={() => message.info('PDF generation coming soon')}>
            Download PDF
          </Button>
        ]}
        width={600}
      >
        {detailModal.invoice && (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Invoice Number">{detailModal.invoice.InvoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(detailModal.invoice.Status)}>{detailModal.invoice.Status.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Customer">{detailModal.invoice.CustomerName || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Email">{detailModal.invoice.CustomerEmail || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Created">{dayjs(detailModal.invoice.CreatedAt).format('MMM D, YYYY')}</Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {detailModal.invoice.DueDate ? dayjs(detailModal.invoice.DueDate).format('MMM D, YYYY') : 'N/A'}
              </Descriptions.Item>
              {detailModal.invoice.QuotationRefNo && (
                <Descriptions.Item label="Order" span={2}>{detailModal.invoice.QuotationRefNo}</Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Subtotal">${detailModal.invoice.Subtotal.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Tax">${detailModal.invoice.Tax.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Total">
                <Text strong>${detailModal.invoice.Total.toFixed(2)} {detailModal.invoice.Currency}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Amount Paid">
                <Text type="success">${detailModal.invoice.AmountPaid.toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Amount Due">
                <Text type={detailModal.invoice.AmountDue > 0 ? 'danger' : undefined}>
                  ${detailModal.invoice.AmountDue.toFixed(2)}
                </Text>
              </Descriptions.Item>
              {detailModal.invoice.PaidAt && (
                <Descriptions.Item label="Paid On">
                  {dayjs(detailModal.invoice.PaidAt).format('MMM D, YYYY HH:mm')}
                </Descriptions.Item>
              )}
            </Descriptions>

            {detailModal.invoice.Description && (
              <>
                <Divider />
                <Text type="secondary">{detailModal.invoice.Description}</Text>
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceList;
